import React, { useEffect, useMemo } from 'react';
import { OVB_OPPORTUNITIES_WIDGET_NAME } from '@utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { fetchData } from '@store/overviewDashboard/kpiData/actions';
import { MetricTitle } from '@components/overview-dashboard/styled';
import {
  activeTabSelector,
  chartDataSelector,
  adContentDataSelector,
} from '@store/overviewDashboard/kpiData/selectors';
import { AdContentTabKey, BenchmarkTabKey } from '@store/overviewDashboard/kpiData/reducer';
import { useHelpPopup } from '@components/overview-dashboard/common/help-popup';
import Content from './Content';
import { MetricWidgetWrapperSub, TitleContainer } from './styled';
import Toggle from './Toggle';
import AdContentTable from './AdContentTable';
import Benchmark from './Benchmark';
import useSetExport from '@src/components/export/useSetExport';
import { getMetricOvbDisplayName } from '@src/utils/metrics';

const SubOpportunities = () => {
  const dispatch = useDispatch();
  const activeTab = useSelector(activeTabSelector);
  const { helpContext, HelpIcon } = useHelpPopup();

  const renderKPITitle = () => (
    <TitleContainer>
      <MetricTitle style={{ flex: 1 }}>{OVB_OPPORTUNITIES_WIDGET_NAME}</MetricTitle>
      <Toggle />
      <HelpIcon />
    </TitleContainer>
  );

  const renderActiveTab = () => {
    if (activeTab === AdContentTabKey) {
      return <AdContentTable helpContext={helpContext} retryHandler={retryHandler} />;
    } else if (activeTab === BenchmarkTabKey) {
      return <Benchmark helpContext={helpContext} retryHandler={retryHandler} />;
    } else {
      return <Content helpContext={helpContext} retryHandler={retryHandler} />;
    }
  };
  useEffect(() => {
    dispatch(fetchData());
  }, []);

  const retryHandler = () => {
    dispatch(fetchData());
  };

  // Export data
  const chartData = useSelector(chartDataSelector);
  const adContentData = useSelector(adContentDataSelector);

  const exportHeaders = useMemo(() => {
    if (activeTab === AdContentTabKey) {
      // Ad Content tab has different columns
      return [
        { key: 'metric', title: 'Metric' },
        { key: 'overall', title: 'Overall' },
        { key: 'withAds', title: 'With Ads' },
        { key: 'withoutAds', title: 'Without Ads' },
      ];
    }
    // Charts tab
    return [
      { key: 'metric', title: 'Metric' },
      { key: 'value', title: 'Value (%)' },
    ];
  }, [activeTab]);

  const exportData = useMemo(() => {
    // Only export data for Charts tab
    if (activeTab !== AdContentTabKey && activeTab !== BenchmarkTabKey && chartData) {
      return Object.keys(chartData).map((metricKey) => {
        const metric = chartData[metricKey];
        const displayName = getMetricOvbDisplayName({ metricKey });

        // Use raw value (not humanized)
        const rawValue = metric?._aggregate;
        const fullValue = rawValue !== undefined && rawValue !== null ? rawValue : 'N/A';

        return {
          metric: displayName,
          value: fullValue,
        };
      });
    }
    return [];
  }, [chartData, activeTab]);

  // Export Ad Content Startup Metrics (PreRoll)
  const exportStartupData = useMemo(() => {
    if (activeTab === AdContentTabKey && adContentData?.preRoll) {
      return adContentData.preRoll.map((row: any) => ({
        metric: row.metricName,
        overall: row.overAll ?? 'N/A',
        withAds: row.WithPreRoll ?? 'N/A',
        withoutAds: row.WithoutPreRoll ?? 'N/A',
      }));
    }
    return [];
  }, [adContentData, activeTab]);

  // Export Ad Content Playback Metrics (Ads)
  const exportPlaybackData = useMemo(() => {
    if (activeTab === AdContentTabKey && adContentData?.ads) {
      return adContentData.ads.map((row: any) => ({
        metric: row.metricName,
        overall: row.overAll ?? 'N/A',
        withAds: row.WithAds ?? 'N/A',
        withoutAds: row.WithoutAds ?? 'N/A',
      }));
    }
    return [];
  }, [adContentData, activeTab]);

  // Export for Charts tab - only populate when Charts tab is active
  useSetExport({
    pageKey: 'overview',
    sectionKey: 'overview-sub-opportunities',
    widgetKey: 'sub-opportunities',
    widgetInfo: { title: OVB_OPPORTUNITIES_WIDGET_NAME },
    headers: activeTab !== AdContentTabKey && activeTab !== BenchmarkTabKey ? exportHeaders : [],
    data: exportData,
  });

  // Export for Ad Content - Startup Metrics - only populate when Ad Content tab is active
  useSetExport({
    pageKey: 'overview',
    sectionKey: 'overview-sub-opportunities',
    widgetKey: 'sub-opportunities-startup',
    widgetInfo: { title: `${OVB_OPPORTUNITIES_WIDGET_NAME} - Ad Content - Startup Metrics` },
    headers: activeTab === AdContentTabKey ? exportHeaders : [],
    data: exportStartupData,
  });

  // Export for Ad Content - Playback Metrics - only populate when Ad Content tab is active
  useSetExport({
    pageKey: 'overview',
    sectionKey: 'overview-sub-opportunities',
    widgetKey: 'sub-opportunities-playback',
    widgetInfo: { title: `${OVB_OPPORTUNITIES_WIDGET_NAME} - Ad Content - Playback Metrics` },
    headers: activeTab === AdContentTabKey ? exportHeaders : [],
    data: exportPlaybackData,
  });

  return (
    <MetricWidgetWrapperSub id="kpi-widget">
      {renderKPITitle()}
      {renderActiveTab()}
    </MetricWidgetWrapperSub>
  );
};

export default React.memo(SubOpportunities);
